import axios from "axios";
import { Backdrop, Button, LinearProgress } from "@mui/material";
import { idFetcher } from "../../utilities/fetcher";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useState } from "react";
import { useProfile } from "../../providers/profile";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";
import Camera from "../media/camera";

export default function TakeDocPic({
  open,
  setOpen,
  onSave,
  isPrivate,
}: {
  open: boolean;
  setOpen: (state: boolean) => void;
  onSave: (details: any) => void;
  isPrivate: boolean;
}) {
  const [uploadProgress, setUploadProgress] = useState(0);

  const { getIdTokenClaims } = useAuth0();
  const { account, viewProfile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();

  const onCancelClick = useCallback(() => {
    setUploadProgress(0);
    setOpen(false);
  }, [setOpen]);

  const onSaveClick = useCallback(
    async (blob: Blob) => {
      const key = encodeURIComponent(
        `${isPrivate ? "private" : "protected"}/${
          isPrivate ? viewProfile?.account?.sub : account?.sub
        }/${uuidv4()}.jpg`
      );
      const upload = await idFetcher(`/api/s3/put/${key}`, getIdTokenClaims);

      try {
        const result = await axios.put(upload.signedUrl, blob, {
          headers: { "content-type": blob.type },
          onUploadProgress: (evt) => {
            if (!evt.total) return setUploadProgress(0);
            setUploadProgress((evt.loaded / evt.total) * 100);
          },
        });

        onSave({ key });
        setOpen(false);
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Problem with file upload. Please try again.", {
          variant: "error",
        });
      } finally {
        setUploadProgress(0);
      }
    },
    [
      getIdTokenClaims,
      account,
      viewProfile,
      enqueueSnackbar,
      isPrivate,
      onSave,
      setOpen,
    ]
  );

  return (
    <Backdrop
      sx={{
        alignItems: "flex-start",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        paddingTop: "4rem",
        paddingBottom: "4rem",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        overflow: "auto",
      }}
      open={open}
    >
      <div
        className="flex flex-col gap-4 bg-white rounded-md p-8"
        style={{ width: "80vw" }}
      >
        <div className="flex-1">
          <Camera active={open} onSave={onSaveClick} />
        </div>
        <div className="flex gap-4 justify-start">
          <Button
            onClick={onCancelClick}
            color="secondary"
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </div>
    </Backdrop>
  );
}
